import { useInfiniteQuery } from "@tanstack/react-query";

import { SIDEBAR_OBSERVABILITY_QUERY_OPTIONS } from "./sidebarObservabilityQueryOptions";

import { buildTracesSearchParams } from "@/components/TracesSearch/buildTracesSearchParams";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { queryKeys } from "@/constants";
import {
  fetchTracesSearchPaginated,
  type TracesSearchPageResponse,
  type TracesSearchParams,
} from "@/queries/traces/fetchTracesSearchPaginated";
import { REQUEST_LOGS_SIDEBAR_PAGE_SIZE } from "@/utils/requestLogUrlParams";

export const SIDEBAR_STRUCTURED_TRACES_SCOPE = "sidebar-structured" as const;

interface TracesSearchPageParam {
  page: number;
  after_key: Record<string, unknown> | null;
}

export interface SidebarStructuredTracesInfiniteData {
  pageParams: TracesSearchPageParam[];
  pages: TracesSearchPageResponse[];
}

export const getSidebarStructuredTracesQueryKey = ({
  userToken,
  tracesSearchParams,
  activeWorkspaceId,
  sidebarPageSize,
}: {
  userToken: string;
  tracesSearchParams: TracesSearchParams;
  activeWorkspaceId: number | null | undefined;
  sidebarPageSize: number;
}) =>
  [
    queryKeys.traces,
    SIDEBAR_STRUCTURED_TRACES_SCOPE,
    userToken,
    tracesSearchParams,
    activeWorkspaceId,
    sidebarPageSize,
  ] as const;

export const createEmptySidebarStructuredTracesInfiniteData = (
  sidebarPageSize: number,
): SidebarStructuredTracesInfiniteData => ({
  pageParams: [{ page: 1, after_key: null }],
  pages: [
    {
      success: true,
      items: [],
      page: 1,
      per_page: sidebarPageSize,
      total: 0,
      total_is_lower_bound: false,
      next_after_key: null,
      pagination_mode: "page",
    },
  ],
});

export const useStructuredSidebarTraces = (enabledFlag = true) => {
  const structuredSearchStore = useStructuredSearchStore();
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const user = useUser();
  const activeWorkspaceId = user?.activeWorkspaceId;
  const sidebarPageSize =
    structuredSearchStore.pageSize > 0
      ? structuredSearchStore.pageSize
      : REQUEST_LOGS_SIDEBAR_PAGE_SIZE;

  const tracesSearchParams = buildTracesSearchParams(structuredSearchStore);

  const queryEnabled =
    enabledFlag &&
    structuredSearchStore.urlHydrationReady &&
    structuredSearchStore.schema.id === "traces" &&
    !!userToken &&
    !!activeWorkspaceId;

  return useInfiniteQuery({
    queryKey: getSidebarStructuredTracesQueryKey({
      userToken,
      tracesSearchParams,
      activeWorkspaceId,
      sidebarPageSize,
    }),
    queryFn: ({ pageParam }) => {
      const { page, after_key }: TracesSearchPageParam = (pageParam as TracesSearchPageParam) ?? {
        page: 1,
        after_key: null,
      };

      return fetchTracesSearchPaginated({
        userToken,
        workspaceId: activeWorkspaceId!,
        page,
        pageSize: sidebarPageSize,
        searchParams: tracesSearchParams,
        afterKey: after_key,
      });
    },
    getNextPageParam: (lastPage): TracesSearchPageParam | undefined => {
      if (lastPage.pagination_mode === "cursor") {
        return lastPage.next_after_key
          ? { page: 1, after_key: lastPage.next_after_key }
          : undefined;
      }
      if (lastPage.items.length < lastPage.per_page) {
        return undefined;
      }
      return { page: lastPage.page + 1, after_key: null };
    },
    enabled: queryEnabled,
    placeholderData: createEmptySidebarStructuredTracesInfiniteData(sidebarPageSize),
    ...SIDEBAR_OBSERVABILITY_QUERY_OPTIONS,
  });
};
